import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import * as Core from '../../../core';
import { IconButton } from '../../../components/buttons-visuals';
import ConfirmModal from '../../../components/confirmModal';
import ImageUploader from '../../../components/imageUploader';
import { SeasonService } from '../../../services/seasonService';

interface SeasonImageUploaderProps {
    aspectRatio?: number;
    contentType: string;
    fallbackUrl?: string;
    header: string;
    helpText: string;
    imageType: Core.Models.SeasonImageType;
    maxFileSize?: number;
    maxImageWidth?: number;
    reloadData: () => Promise<void>;
    requireCrop?: boolean;
    seasonId: string;
    text: string;
    url?: string;
}

const SeasonImageUploader = (props: SeasonImageUploaderProps): JSX.Element => {
    const {
        aspectRatio,
        contentType,
        fallbackUrl,
        header,
        helpText,
        imageType,
        maxFileSize,
        maxImageWidth,
        reloadData,
        requireCrop,
        seasonId,
        text,
        url,
    } = props;
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const displayUrl = url || fallbackUrl;

    const deleteImage = useCallback(async () => {
        await SeasonService.deleteImage(seasonId, imageType);
        await reloadData();
    }, [imageType, reloadData, seasonId]);

    const uploadImage = useCallback(
        async (imageData: string) => {
            await SeasonService.uploadImage(seasonId, { imageData, imageType });
            await reloadData();
        },
        [imageType, reloadData, seasonId]
    );

    return (
        <section className="season-image-uploader">
            <h4 className="heading-4">{header}</h4>
            <p className="text-small color-gray">{helpText}</p>
            {!!displayUrl && <img className="season-image-uploader__image" src={displayUrl} alt="" />}
            {!url && !!fallbackUrl && <p className="text-small">Using the game image.</p>}
            <div className="disp-flex align-center justify-start">
                <ImageUploader
                    {...{
                        aspectRatio,
                        contentType,
                        className: 'season-image-uploader__image-uploader mr',
                        flush: true,
                        maxFileSize,
                        maxImageWidth,
                        icon: 'upload',
                        requireCrop,
                        text,
                        uploadImage,
                    }}
                />
                {!!url && (
                    <IconButton
                        as="button"
                        buttonLabel="Delete image"
                        buttonSize="medium"
                        color="destructive"
                        onClick={() => setIsConfirmingDelete(true)}
                    >
                        <FontAwesomeIcon icon={['fas', 'trash']} />
                    </IconButton>
                )}
            </div>

            {isConfirmingDelete && (
                <ConfirmModal
                    confirmText="Yes"
                    onCancel={() => setIsConfirmingDelete(false)}
                    onConfirm={async () => {
                        await deleteImage();
                        setIsConfirmingDelete(false);
                    }}
                    title="Are you sure"
                >
                    <p>Are you sure you want to delete this image? The game image will be used instead.</p>
                </ConfirmModal>
            )}
        </section>
    );
};

export default SeasonImageUploader;
